"use client";

import React, { useMemo, useCallback, useState } from "react";
import { ResponsiveModal, MODAL_IDS } from "@wtc/ui";
import { useQuery } from "@tanstack/react-query";
import { getSupabase } from "@/lib/hooks/api/utils";
import { useUpdateContact } from "@/features/contacts/hooks";
import { useToast } from "@/components/ui/toast";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Loader2, Search, Link as LinkIcon } from "lucide-react";

interface LinkContactModalProps {
  companyId: string;
  companyName: string;
  isOpen: boolean;
  onClose: () => void;
}

interface Contact {
  id: string;
  name: string;
  email: string;
  phone?: string;
  status?: string;
  company_id?: string;
  company_name?: string;
}

export function LinkContactModal({ companyId, companyName, isOpen, onClose }: LinkContactModalProps) {
  const { mutate: updateContact, isPending: isUpdating } = useUpdateContact();
  const { toast } = useToast();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedContactId, setSelectedContactId] = useState<string | null>(null);

  // Fetch all contacts
  const { data: contactsData, isLoading: isLoadingContacts } = useQuery({
    queryKey: ["contacts-for-linking", companyId],
    queryFn: async () => {
      const supabase = getSupabase();
      const { data, error } = await supabase
        .from("contacts")
        .select("id, name, email, phone, status, company_id, company_name")
        .order("name", { ascending: true });

      if (error) throw error;
      return data;
    },
    enabled: !!companyId && isOpen,
  });

  // Filter contacts based on search term and exclude those already linked to this company
  const filteredContacts = useMemo(() => {
    if (!contactsData) return [];

    return contactsData.filter((contact) => {
      // Exclude contacts already linked to this company
      if (contact.company_id === companyId) return false;

      // Filter by search term
      const searchLower = searchTerm.toLowerCase();
      return (
        contact.name.toLowerCase().includes(searchLower) ||
        contact.email.toLowerCase().includes(searchLower) ||
        (contact.phone && contact.phone.includes(searchTerm))
      );
    });
  }, [contactsData, searchTerm, companyId]);

  const handleSelectContact = useCallback((contact: Contact) => {
    setSelectedContactId(contact.id);
  }, []);

  const handleLinkContact = useCallback(async () => {
    if (!selectedContactId) {
      toast({
        type: "error",
        title: "Error",
        description: "Please select a contact",
      });
      return;
    }

    const selectedContact = contactsData?.find((c) => c.id === selectedContactId);
    if (!selectedContact) return;

    try {
      await updateContact(
        {
          id: selectedContactId,
          updates: {
            company_id: companyId,
            company_name: companyName,
          },
        },
        {
          onSuccess: () => {
            toast({
              type: "success",
              title: "Success",
              description: `${selectedContact.name} has been linked to ${companyName}`,
            });
            onClose();
            setSelectedContactId(null);
            setSearchTerm("");
          },
          onError: () => {
            toast({
              type: "error",
              title: "Error",
              description: "Failed to link contact. Please try again.",
            });
          },
        }
      );
    } catch (error) {
      toast({
        type: "error",
        title: "Error",
        description: "Failed to link contact. Please try again.",
      });
    }
  }, [selectedContactId, contactsData, companyId, companyName, updateContact, toast, onClose]);

  const handleClose = useCallback(() => {
    onClose();
    setSelectedContactId(null);
    setSearchTerm("");
  }, [onClose]);

  return (
    <ResponsiveModal
      globalModalId={MODAL_IDS.companies.linkContact}
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          handleClose();
        }
      }}
      title="Link Contact"
      description={`Select a contact to link to ${companyName}`}
      maxWidth="2xl"
    >
      <div className="space-y-4">
        {/* Search Input */}
        <div className="relative">
          <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by name, email, or phone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            disabled={isLoadingContacts || isUpdating}
          />
        </div>

        {/* Contacts List */}
        <ScrollArea className="h-[400px] border rounded-lg p-4">
          {isLoadingContacts ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : filteredContacts.length === 0 ? (
            <div className="flex items-center justify-center h-full text-muted-foreground">
              <p>{searchTerm ? "No contacts found" : "No available contacts"}</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredContacts.map((contact) => (
                <button type="button"
                  key={contact.id}
                  onClick={() => handleSelectContact(contact)}
                  className={`w-full text-left p-3 rounded-lg border-2 transition-colors ${
                    selectedContactId === contact.id
                      ? "border-blue-500 bg-blue-50 dark:bg-blue-950/40"
                      : "border-transparent hover:bg-gray-50 dark:hover:bg-gray-800"
                  }`}
                  disabled={isUpdating}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{contact.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{contact.email}</p>
                      {contact.phone && (
                        <p className="text-xs text-muted-foreground">{contact.phone}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {contact.status && (
                        <Badge variant="outline" className="text-xs">
                          {contact.status}
                        </Badge>
                      )}
                      {contact.company_name && (
                        <Badge variant="secondary" className="text-xs">
                          {contact.company_name}
                        </Badge>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </ScrollArea>

        {/* Action Buttons */}
        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isUpdating}
          >
            Cancel
          </Button>
          <Button
            onClick={handleLinkContact}
            disabled={!selectedContactId || isUpdating}
            className="gap-2"
          >
            {isUpdating ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Linking...
              </>
            ) : (
              <>
                <LinkIcon className="h-4 w-4" />
                Link Contact
              </>
            )}
          </Button>
        </div>
      </div>
    </ResponsiveModal>
  );
}
